import React, { useEffect } from 'react'
import { useFormikContext } from 'formik'
import * as Yup from 'yup'

import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'

import AddressAutocomplete from 'components/common/AddressAutocomplete'

import { DELIVERY_TYPES } from 'utils/constants'

export const addressKeys = {
  address: 'address_line_1',
  addressalt: 'address_line_2',
  city: 'city',
  state: 'state',
  country: 'country',
  zip: 'zip_code',
}

export const optionalKeys = ['address_line_2']

export const validationSchemas = [
  Yup.object().shape({
    company_name: Yup.string().required('Company Name is required'),
    name: Yup.string().required('Name is required'),
    address_line_1: Yup.string()
      .required('Address is required')
      .test(
        'no-pobox',
        'PO Box addresses are not allowed for mail delivery',
        function (value) {
          if (!value) return true // Let the required validation handle empty values
          const poBoxPattern = /^(p\.?o\.?\s*box|po\s*box|post\s*office\s*box)/i
          return !poBoxPattern.test(value)
        }
      ),
    country: Yup.string().required('Country is required'),
    city: Yup.string().required('City is required'),
    state: Yup.string().required('State is required'),
    zip_code: Yup.string().required('ZIP Code is required'),
  }),
  Yup.object().shape({
    email: Yup.string()
      .email('Invalid email address')
      .required('Email is required'),
    name: Yup.string().required('Name is required'),
  }),
]

function EmailFields() {
  const { values, errors, touched, setFieldValue, handleBlur } =
    useFormikContext()

  const handleChange = (e) => {
    setFieldValue(e.target.name, e.target.value)
  }

  return (
    <Grid container spacing={4}>
      <Grid item xs={12}>
        <TextField
          fullWidth
          name="name"
          label="Name"
          value={values.name || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.name && Boolean(errors.name)}
          helperText={touched.name && errors.name}
          required
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          name="email"
          label="Email"
          value={values.email || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.email && Boolean(errors.email)}
          helperText={touched.email && errors.email}
          required
        />
      </Grid>
    </Grid>
  )
}

function AddressFields({ compact }) {
  const { values, errors, touched, setFieldValue, handleBlur } =
    useFormikContext()

  const handleChange = (e) => {
    setFieldValue(e.target.name, e.target.value)
  }

  useEffect(() => {
    if (!values?.country) {
      setFieldValue('country', 'USA')
    }
  }, [])

  return (
    <Grid container spacing={4}>
      <Grid item xs={12}>
        <TextField
          fullWidth
          name="company_name"
          label="Company Name"
          value={values?.company_name || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.company_name && Boolean(errors.company_name)}
          helperText={touched.company_name && errors.company_name}
          required
          size={compact ? 'small' : undefined}
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          fullWidth
          name="name"
          label="Recipient Name"
          value={values?.name || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.name && Boolean(errors.name)}
          helperText={touched.name && errors.name}
          required
          size={compact ? 'small' : undefined}
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          fullWidth
          name="country"
          label="Country"
          value={values?.country || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.country && Boolean(errors.country)}
          helperText={touched.country && errors.country}
          required
          size={compact ? 'small' : undefined}
        />
      </Grid>
      <Grid item xs={12}>
        <AddressAutocomplete
          addressKeys={addressKeys}
          textFieldProps={{
            size: compact ? 'small' : undefined,
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          name="address_line_2"
          label="Address Line 2"
          value={values?.address_line_2 || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.address_line_2 && Boolean(errors.address_line_2)}
          helperText={touched.address_line_2 && errors.address_line_2}
          size={compact ? 'small' : undefined}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          name="address_line_3"
          label="Address Line 3"
          value={values?.address_line_3 || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.address_line_3 && Boolean(errors.address_line_3)}
          helperText={touched.address_line_3 && errors.address_line_3}
          size={compact ? 'small' : undefined}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          name="address_line_4"
          label="Address Line 4"
          value={values?.address_line_4 || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.address_line_4 && Boolean(errors.address_line_4)}
          helperText={touched.address_line_4 && errors.address_line_4}
          size={compact ? 'small' : undefined}
        />
      </Grid>
      <Grid item xs={4}>
        <TextField
          fullWidth
          name="city"
          label="City"
          value={values?.city || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.city && Boolean(errors.city)}
          helperText={touched.city && errors.city}
          required
          size={compact ? 'small' : undefined}
        />
      </Grid>
      <Grid item xs={4}>
        <TextField
          fullWidth
          name="state"
          label="State"
          value={values?.state || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.state && Boolean(errors.state)}
          helperText={touched.state && errors.state}
          required
          size={compact ? 'small' : undefined}
        />
      </Grid>
      <Grid item xs={4}>
        <TextField
          fullWidth
          name="zip_code"
          label="ZIP Code"
          value={values?.zip_code || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.zip_code && Boolean(errors.zip_code)}
          helperText={touched.zip_code && errors.zip_code}
          required
          size={compact ? 'small' : undefined}
        />
      </Grid>
    </Grid>
  )
}

function AddressForm({ activeIndex = DELIVERY_TYPES.MAIL, compact }) {
  return (
    <>
      {activeIndex === DELIVERY_TYPES.MAIL && (
        <AddressFields compact={compact} />
      )}
      {activeIndex === DELIVERY_TYPES.EMAIL && (
        <EmailFields compact={compact} />
      )}
    </>
  )
}

export default AddressForm
